import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setOrderData } from "../ui/uiSlice"
import { 
  fetchCart, 
  updateItemQuantity, 
  removeCartItem, 
  deleteCart, 
  applyPromoCode,
  resetPromo,
  reapplyPromoCode
} from "./cartSlice"
import type { RootState, AppDispatch } from "../../store"
import { RefreshCw } from "lucide-react"
import { useTheme } from "../../contexts/ThemeContext"
import AddMoreButton from "../../components/ui/AddMoreButton"
import NotesModal from "../../components/ui/NotesModal"
import { CartItemRow } from "../../components/ui/CartItemRow"
import { useTranslation } from "../../hooks/useTranslation"
import CurrencyDisplay from "../../components/ui/CurrencyDisplay"


export default function CartScreen() {
  const dispatch = useDispatch<AppDispatch>()
  const { 
    items, 
    apiCartData, 
    subtotal, 
    total, 
    isLoading, 
    error, 
    updatingItemId, 
    removingItemId, 
    deletingCart,
    promo 
  } = useSelector((state: RootState) => state.cart)
  const { token } = useSelector((state: RootState) => state.auth)
  const { primaryColor } = useTheme()
  const { t, currentLanguage: language, isRTL } = useTranslation()

  const [sharedNotes, setSharedNotes] = useState<string>("")
  const [showNotesModal, setShowNotesModal] = useState(false)

  // Promo code input state (for UI only)
  const [promoCodeInput, setPromoCodeInput] = useState<string>("")

  // Load cart data on mount
  useEffect(() => {
    if (token) {
      dispatch(fetchCart(token))
    }
  }, [dispatch, token])
  
  // Re-enable automatic reapplication with improvements to prevent excessive calls
  useEffect(() => {
    // Only reapply if there's an active promo code and user is authenticated
    if (token && promo.code && promo.discount > 0) {
      
      // Add a delay to avoid rapid reapplication and ensure cart state is stable
      const timeoutId = setTimeout(() => {
        dispatch(reapplyPromoCode({ token }))
      }, 1000)
      
      return () => clearTimeout(timeoutId)
    }
  }, [subtotal, items.length, token, promo.code, promo.discount, dispatch])

  const navigateToScreen = (screen: "home" | "checkout") => {
    // Use the navigation utility instead of direct window.location
    const { navigateTo } = require("../../utils/navigation")
    navigateTo(screen)
  }

  const handleQuantityChange = async (id: string, newQuantity: number) => {
    if (newQuantity < 1 || !apiCartData || !token) return
    
    const apiItem = apiCartData.items.find(item => item.item_id.toString() === id)
    if (!apiItem) return


    // Update item quantity first
    const result = await dispatch(updateItemQuantity({
      cartId: apiCartData.id,
      cartItemId: apiItem.id, // استخدم id (cartItemId) وليس item_id
      quantity: newQuantity,
      token
    }))
    
    // Re-enable automatic reapplication after quantity update
    if (updateItemQuantity.fulfilled.match(result) && promo.code && promo.discount > 0) {
   
      
      // Wait a bit for the cart state to update, then reapply promo
      setTimeout(() => {
        dispatch(reapplyPromoCode({ token }))
      }, 500)
    }
    
    // 🚀 إضافة: تحديث بيانات السلة من API بعد تعديل الكمية
    // هذا يضمن أن الأسعار والأسعار الفرعية تتزامن مع التغييرات
    if (updateItemQuantity.fulfilled.match(result)) {
      
      // انتظر قليلاً ثم قم بتحديث بيانات السلة من API
      setTimeout(() => {
        dispatch(fetchCart(token))
      }, 300)
    }
  }

  const handleRemoveItem = async (id: string) => {
    if (!apiCartData || !token) return
    
    const apiItem = apiCartData.items.find(item => item.item_id.toString() === id)
    if (!apiItem) return

    // Remove item first
    const result = await dispatch(removeCartItem({
      cartId: apiCartData.id,
      cartItemId: apiItem.id,
      itemId: id,
      token
    }))
    
    // Re-enable automatic reapplication after item removal
    if (removeCartItem.fulfilled.match(result) && promo.code && promo.discount > 0) {
      
      // Wait a bit for the cart state to update, then reapply promo
      setTimeout(() => {
        dispatch(reapplyPromoCode({ token }))
      }, 500)
    }
    
    
    if (removeCartItem.fulfilled.match(result)) {

      setTimeout(() => {
        dispatch(fetchCart(token))
      }, 300)
    }
  }

  const handleCheckout = () => {
    const orderData: any = {}
    
    if (sharedNotes.trim()) {
      orderData.notes = sharedNotes.trim()
    }
    
    if (promo.code && promo.status === 'applied') {
      orderData.coboneCode = promo.code
    }
    
    if (Object.keys(orderData).length > 0) {
      dispatch(setOrderData(orderData))
    }
    
    navigateToScreen("checkout")
  }

  const handleBack = () => navigateToScreen("home")

  const handleDeleteCart = async () => {
    if (!apiCartData || !token) return
    
    const result = await dispatch(deleteCart({ cartId: apiCartData.id, token }))
    if (deleteCart.fulfilled.match(result)) {
      setTimeout(() => navigateToScreen("home"), 500)
    }
  }

  const handleOpenNotesModal = () => setShowNotesModal(true)
  const handleCloseNotesModal = () => setShowNotesModal(false)
  const handleSaveNote = (note: string) => setSharedNotes(note)

  // Promo code handlers
  const handleApplyPromoCode = async () => {
    if (!promoCodeInput.trim() || !token) return
    
    dispatch(applyPromoCode({
      code: promoCodeInput.trim(),
      subtotal,
      token
    }))
  }

  const handleRemovePromoCode = () => {
    dispatch(resetPromo())
    setPromoCodeInput("")
  }

  const clearPromoInput = () => {
    setPromoCodeInput("")
    dispatch(resetPromo())
  }

  const finalTotal = Math.max(0, total)

  if (isLoading) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="bg-white min-h-screen">
        <div className="bg-white">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={handleBack} className={`flex items-center text-gray-700 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className="border border-gray-200 rounded-xl p-1.5 text-black">
                <img src="/back.svg" alt={t('common.back')} className="h-6 w-6" />
              </div>
              <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('cart.basket')}</span>
            </button>
            {apiCartData && (
<button
  onClick={handleDeleteCart}
  disabled={deletingCart}
  className="inline-flex items-center gap-1 h-6 px-3 rounded-lg
             bg-red-500/10 text-red-500 text-xs font-medium
             font-['IBM_Plex_Sans_Arabic'] leading-none
             disabled:opacity-50 disabled:cursor-not-allowed"
>
  {deletingCart && (
    <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
  )}
  <span>{t('cart.deleteCart')}</span>
</button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
            <p className="text-gray-600">{t('cart.loadingCart')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="bg-white min-h-screen">
        <div className="bg-white">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={handleBack} className="flex items-center text-gray-700">
              <div className="border border-gray-200 rounded-xl p-1.5 text-black">
                {isRTL ? (
                  <img src="/back.svg" alt={t('common.back')} className="h-6 w-6" />
                ) : (
                  <img src="/back_lift.svg" alt={t('common.back')} className="h-6 w-6" />
                )}
              </div>
            </button>
            <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('cart.basket')}</span>
          </div>
            {apiCartData && apiCartData.items && apiCartData.items.length > 0 && (
<button
  onClick={handleDeleteCart}
  disabled={deletingCart}
  className="inline-flex items-center gap-1 h-6 px-3 rounded-lg
             bg-red-500/10 text-red-500 text-xs font-medium
             font-['IBM_Plex_Sans_Arabic'] leading-none
             disabled:opacity-50 disabled:cursor-not-allowed"
>
  {deletingCart && (
    <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
  )}
  <span>{t('cart.deleteCart')}</span>
</button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3 text-center px-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center">
              <img src="/not_salla.svg" alt={t('cart.empty')} className="w-16 h-16" />
            </div>
            <h3 className="font-bold text-lg">{t('cart.empty')}</h3>
            <button onClick={handleBack} className="px-6 py-2 rounded-lg text-white font-bold flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
              {t('cart.continueShopping')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="bg-white min-h-screen">

      
      {/* Header */}
      <div className="bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={handleBack} className="flex items-center text-gray-700">
              <div className="border border-gray-200 rounded-xl p-1.5 text-black">
                {isRTL ? (
                  <img src="/back.svg" alt={t('common.back')} className="h-6 w-6" />
                ) : (
                  <img src="/back_lift.svg" alt={t('common.back')} className="h-6 w-6" />
                )}
              </div>
            </button>
            <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('cart.basket')}</span>
          </div>
          {apiCartData && apiCartData.items && apiCartData.items.length > 0 && (
<button
  onClick={handleDeleteCart}
  disabled={deletingCart}
  className="inline-flex items-center gap-1 h-6 px-3 rounded-lg
             bg-red-500/10 text-red-500 text-xs font-medium
             font-['IBM_Plex_Sans_Arabic'] leading-none
             disabled:opacity-50 disabled:cursor-not-allowed"
>
  {deletingCart && (
    <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
  )}
  <span>{t('cart.deleteCart')}</span>
</button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-0 pb-20">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[80vh]">
            <img src="/not_salla.svg" alt={t('cart.empty')} className="w-22 h-22 mb-6" />
            <div className="flex flex-col items-center gap-4">
              <p className="text-lg font-medium">{t('cart.empty')}</p>
              <button onClick={handleBack} className="text-white px-6 py-2 rounded-xl transition-colors font-medium" style={{ backgroundColor: primaryColor }}>
                {t('cart.continueShopping')}
              </button>
            </div>
          </div>
        ) : (
          <>
           {/* Items */}
           <div className="bg-white rounded-lg shadow-sm  overflow-y-auto max-h-[calc(100vh-200px)]">
             {/* تدرج أبيض خفيف في البداية */}
             <div className="h-6 bg-gradient-to-b from-white via-white/60 to-transparent pointer-events-none"></div>
             
             {items.map((item) => {
               const apiItem = apiCartData?.items.find(ai => ai.item_id.toString() === item.id);
               
               return (
                 <CartItemRow
                   key={item.id}
                   item={item}
                   apiItem={apiItem}
                   primaryColor={primaryColor}
                   deletingItemId={removingItemId}
                   updatingQuantityId={updatingItemId}
                   onRemove={handleRemoveItem}
                   onChangeQty={handleQuantityChange}
                   isRTL={isRTL}
                 />
               );
             })}
             {/* تدرج أبيض خفيف في النهاية */}
             <div className="h-10"></div>

           </div>

           <div className="relative">
  {/* cart items */}
  {items.length >= 5 && (
    <div className="absolute left-0 right-0 h-14 bottom-0 bg-gradient-to-t from-[#F9F9F5] via-[#F9F9F5]/90 to-transparent pointer-events-none" />
  )}
</div>


            {/* Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-3">
            <div data-layer="Gradient" className="Gradient w-96 h-10 left-0 top-0 absolute bg-gradient-to-b from-white/0 to-white"></div>

              <div className="container mx-auto">

                  {/* Notes */}
               <div className="bg-gray-50 rounded-xl mb-1 ">
                  <div className="p-2 px-3 border border-gray-200 rounded-2xl bg-white/80 backdrop-blur-xl">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className={`w-6 h-6 flex-shrink-0 ${isRTL ? 'ml-3 mr-0' : 'ml-0 mr-3'}`}>
                     

                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M6.4247 5.96656C6.58139 6.49615 6.27908 7.05249 5.74949 7.20917C4.16178 7.67891 3.67116 7.99964 3.42994 8.4151C3.27579 8.68059 3.19906 9.0304 3.28685 9.72268C3.37837 10.4444 3.62421 11.3754 3.98944 12.7462L5.00179 16.5459C5.36703 17.9168 5.61699 18.8466 5.89648 19.5175C6.16466 20.1613 6.40442 20.424 6.66765 20.5751C6.93172 20.7267 7.28257 20.8022 7.97966 20.7109C8.70491 20.616 9.64085 20.366 11.017 19.9951L11.4897 19.8678C12.023 19.7241 12.5718 20.0399 12.7154 20.5731C12.8591 21.1064 12.5433 21.6552 12.0101 21.7989L11.4696 21.9445C10.1784 22.2925 9.11286 22.5796 8.23925 22.694C7.32225 22.814 6.46836 22.7668 5.6719 22.3096C4.87461 21.8519 4.40515 21.1385 4.05028 20.2866C3.71269 19.4763 3.42979 18.4143 3.08742 17.1292L2.03867 13.1929C1.6962 11.9076 1.41325 10.8457 1.30274 9.97429C1.18655 9.05805 1.23873 8.20589 1.70034 7.41087C2.38635 6.22933 3.65428 5.74336 5.18209 5.29135C5.71168 5.13467 6.26802 5.43697 6.4247 5.96656Z" fill={primaryColor}/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M20.4252 3.28231C19.627 2.95101 18.5744 2.67059 17.2693 2.32287L16.2093 2.04043C14.904 1.69265 13.8515 1.4122 12.9938 1.30229C12.1032 1.18818 11.3123 1.241 10.582 1.66025C9.85111 2.07986 9.40858 2.73583 9.06201 3.5614C8.72858 4.35566 8.44641 5.40287 8.09685 6.70016L7.04511 10.6032C6.69553 11.9004 6.41332 12.9476 6.30269 13.8013C6.18771 14.6885 6.24088 15.4779 6.66385 16.2064C7.08645 16.9342 7.74612 17.3737 8.57468 17.7176C9.37284 18.0489 10.4254 18.3293 11.7305 18.677L12.7906 18.9595C14.0958 19.3073 15.1483 19.5877 16.006 19.6976C16.8966 19.8117 17.6876 19.7589 18.4178 19.3396C19.1487 18.92 19.5913 18.2641 19.9378 17.4385C20.2713 16.6442 20.5534 15.597 20.903 14.2997L21.9547 10.3967C22.3043 9.09953 22.5865 8.05231 22.6972 7.19861C22.8121 6.31139 22.759 5.52199 22.336 4.79352C21.9134 4.06567 21.2537 3.62623 20.4252 3.28231ZM13.0738 6.29109C12.6735 6.18445 12.2626 6.42247 12.156 6.82272C12.0493 7.22297 12.2873 7.63389 12.6876 7.74053L17.4605 9.01221C17.8608 9.11885 18.2717 8.88084 18.3783 8.48059C18.485 8.08034 18.2469 7.66942 17.8467 7.56278L13.0738 6.29109ZM12.0507 10.0879C11.6504 9.98125 11.2395 10.2193 11.1329 10.6195C11.0262 11.0198 11.2642 11.4307 11.6645 11.5373L14.0509 12.1732C14.4512 12.2798 14.8621 12.0418 14.9687 11.6415C15.0754 11.2413 14.8374 10.8304 14.4371 10.7237L12.0507 10.0879Z" fill={primaryColor}/>
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900">{t('cart.notes')}</p>
                          <p className="text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                            {(sharedNotes || t('cart.addNotesToOrder'))
                              .split(' ')
                              .slice(0, 4)
                              .join(' ')
                              + (sharedNotes && sharedNotes.split(' ').length > 4 ? '...' : '')
                            }
                          </p>
                        </div>
                      </div>
                      {sharedNotes ? (
                        <div className="flex gap-2 flex-shrink-0">
                       
                          <button 
                            onClick={handleOpenNotesModal} 
                            className="p-2 transition-colors"
                            style={{ color: '#6B7280' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = primaryColor || '#C44536'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#6B7280'
                            }}
                          >
                          <svg   width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <mask id="mask0_7779_1942" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                                          <rect width="20" height="20" fill="#D9D9D9"/>
                                          </mask>
                                          <g mask="url(#mask0_7779_1942)">
                                          <path d="M4.16663 15.8333H5.21788L13.7483 7.30288L12.697 6.25163L4.16663 14.782V15.8333ZM3.66996 17.0833C3.45649 17.0833 3.2776 17.0111 3.13329 16.8666C2.98885 16.7223 2.91663 16.5434 2.91663 16.33V14.8862C2.91663 14.683 2.95565 14.4893 3.03371 14.3052C3.11163 14.121 3.21899 13.9605 3.35579 13.8237L13.9087 3.27558C14.0347 3.16114 14.1738 3.07274 14.326 3.01038C14.4784 2.94788 14.6381 2.91663 14.8052 2.91663C14.9723 2.91663 15.1341 2.94628 15.2906 3.00558C15.4473 3.06489 15.5859 3.15919 15.7066 3.2885L16.7243 4.31892C16.8536 4.43961 16.9458 4.5785 17.0008 4.73558C17.0558 4.89267 17.0833 5.04975 17.0833 5.20683C17.0833 5.37447 17.0547 5.5344 16.9975 5.68663C16.9402 5.83899 16.8492 5.97815 16.7243 6.10413L6.17621 16.6441C6.0394 16.7809 5.87892 16.8883 5.69475 16.9662C5.51058 17.0443 5.3169 17.0833 5.11371 17.0833H3.66996ZM13.2135 6.78642L12.697 6.25163L13.7483 7.30288L13.2135 6.78642Z" fill="currentColor"/>
                                          </g>
                                          </svg>
                          </button>
                          <button 
                            onClick={() => handleSaveNote('')} 
                            className="p-2 transition-colors"
                            style={{ color: '#6B7280' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = primaryColor || '#C44536'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#6B7280'
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <mask id="mask0_7779_1938" style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                                <rect width="20" height="20" fill="#D9D9D9"/>
                              </mask>
                              <g mask="url(#mask0_7779_1938)">
                                <path d="M6.08979 17.0834C5.67549 17.0834 5.32083 16.9359 5.02583 16.6409C4.73083 16.3459 4.58333 15.9912 4.58333 15.5769V5.00002H4.375C4.19792 5.00002 4.04951 4.94009 3.92979 4.82023C3.80993 4.70037 3.75 4.5519 3.75 4.37481C3.75 4.19759 3.80993 4.04919 3.92979 3.92961C4.04951 3.80988 4.19792 3.75002 4.375 3.75002H7.5C7.5 3.546 7.57188 3.37211 7.71563 3.22836C7.85924 3.08475 8.03306 3.01294 8.23708 3.01294H11.7629C11.9669 3.01294 12.1408 3.08475 12.2844 3.22836C12.4281 3.37211 12.5 3.546 12.5 3.75002H15.625C15.8021 3.75002 15.9505 3.80995 16.0702 3.92981C16.1901 4.04968 16.25 4.19815 16.25 4.37523C16.25 4.55245 16.1901 4.70086 16.0702 4.82044C15.9505 4.94016 15.8021 5.00002 15.625 5.00002H15.4167V15.5769C15.4167 15.9912 15.2692 16.3459 14.9742 16.6409C14.6792 16.9359 14.3245 17.0834 13.9102 17.0834H6.08979ZM14.1667 5.00002H5.83333V15.5769C5.83333 15.6518 5.85736 15.7132 5.90542 15.7613C5.95347 15.8093 6.01493 15.8334 6.08979 15.8334H13.9102C13.9851 15.8334 14.0465 15.8093 14.0946 15.7613C14.1426 15.7132 14.1667 15.6518 14.1667 15.5769V5.00002ZM8.46188 14.1667C8.63896 14.1667 8.78736 14.1068 8.90708 13.9871C9.02667 13.8672 9.08646 13.7188 9.08646 13.5417V7.29169C9.08646 7.11461 9.02653 6.96613 8.90667 6.84627C8.78694 6.72655 8.63847 6.66669 8.46125 6.66669C8.28417 6.66669 8.13576 6.72655 8.01604 6.84627C7.89646 6.96613 7.83667 7.11461 7.83667 7.29169V13.5417C7.83667 13.7188 7.89653 13.8672 8.01625 13.9871C8.13611 14.1068 8.28465 14.1667 8.46188 14.1667ZM11.5387 14.1667C11.7158 14.1667 11.8642 14.1068 11.984 13.9871C12.1035 13.8672 12.1633 13.7188 12.1633 13.5417V7.29169C12.1633 7.11461 12.1035 6.96613 11.9837 6.84627C11.8639 6.72655 11.7153 6.66669 11.5381 6.66669C11.361 6.66669 11.2126 6.72655 11.0929 6.84627C10.9733 6.96613 10.9135 7.11461 10.9135 7.29169V13.5417C10.9135 13.7188 10.9735 13.8672 11.0933 13.9871C11.2131 14.1068 11.3615 14.1667 11.5387 14.1667Z" fill="var(--icon-secondary, #525866)"/>
                              </g>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button className="px-3 py-1 text-sm font-medium" style={{ color: primaryColor }} onClick={handleOpenNotesModal}>
                          {t('common.add')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-3">
                  <span className="text-center text-text-secondary text-base font-normal">{t('cart.subtotal')}</span>
                  <span className="font-bold text-lg flex items-center gap-1.5" style={{ color: primaryColor }} dir="ltr">
                    <CurrencyDisplay iconStyle={{ color: primaryColor }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19" fill="none">
                        <path d="M15.9999 15.0621C15.8942 15.9172 15.8479 16.2882 15.4531 17.1217L9.38959 18.3737C9.52897 17.4728 9.71455 16.7777 10.0165 16.361L15.9999 15.0621ZM7.55463 9.16565L9.36615 8.77307V3.04749C10.0413 2.28964 10.4565 1.94932 11.2714 1.51917V8.35901L15.9999 7.33264C15.8942 8.1875 15.8478 8.55802 15.4531 9.39124L11.2714 10.275V12.1989L15.9999 11.1979C15.8942 12.053 15.848 12.4239 15.4531 13.2574L11.2714 14.1207V14.1383L9.36615 14.5319V10.6774L7.55463 11.0602V13.4899L7.52338 13.4957C7.10666 14.2265 6.51821 15.1044 5.95111 15.8053L0 16.9391C0.0533004 16.1734 0.164418 15.7416 0.510681 14.9703L5.64935 13.8561V11.4635L0.886658 12.4713C0.940015 11.7056 1.05218 11.2747 1.39838 10.5035L5.64935 9.57971V1.98206C6.32448 1.22421 6.73971 0.883893 7.55463 0.453735V9.16565Z" fill={primaryColor}/>
                      </svg>
                    </CurrencyDisplay>
                    <span>{finalTotal}</span>
                  </span>
                </div>
                <div className="flex gap-3 items-center">
                  <AddMoreButton onClick={handleBack} />
                  <button onClick={handleCheckout} className="flex-1 text-white py-2 rounded-lg font-bold transition-colors" style={{ backgroundColor: primaryColor }}>
                    {t('cart.goToPayment')}
                  </button>
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={handleCloseNotesModal} onSave={handleSaveNote} initialNote={sharedNotes} />
      
 
    </div>
  )
}


//



                